/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Builds the ql/list data provider configuration of QL-Widget-Builder widgets - request with view or
 *   simple pipe (side-car), includeCount per Q-A, paging per preview/tile size
 */
import {Injectable} from '@angular/core';
import {WidgetData} from "../../../shared/widget/widget-data";
import {WidgetDataParam} from "../../../shared/widget/widget-data-param";
import {QlWidgetFactory} from "../../../shared/widget/service/ql-widget.factory";
import {QlQueryDto} from "../../../shared/ql/data/dto/ql-query.dto";
import {QlJoinDto} from "../../../shared/ql/data/dto/ql-join.dto";
import {QlQueryContentFieldDto} from "../../../shared/ql/data/dto/ql-query-content-field.dto";
import {PagingDto} from "../../../shared/dto/model/paging.dto";
import {
    QL_BUILDER_POLICY_LIMITS,
    QlBuilderFieldPatterns,
    QlBuilderQueryConverter,
} from "../../../shared/ql/builder/ql-builder-query-converter";
import {QlBuilderModel} from "../../../shared/ql/builder/ql-builder-model";
import {UiWidgetDto} from "../../model/dto/entity/ui-widget.dto";
import {UiDataSourceEnum} from "../../enum/ui-data-source-enum.enum";
import {UiHomeTileSizeEnum} from "../../model/dto/enum/ui-home-tile-size.enum";
import {UiHomeTileMetrics} from "../../logic/ui-home-tile-metrics";
import {UiHomeWidgetBuildError} from "../../logic/ui-home-widget-build-error";
import {UiQlWidgetMappingKeyLabels} from "../../logic/ui-ql-widget-mapping-key-labels";
import {UiQlWidgetMapping, UiQlWidgetType} from "../../model/dto/api/ui-ql-widget-mapping.dto";
import {CoreMetaExternalSideCarDto} from "../../../shared/meta/dto/meta-external-side-car.dto";
import {UI_QL_WIDGET_TYPE_REGISTRY} from "../../logic/ui-ql-widget-type-registry";
import {UiQlWidgetTypeRegistryEntry} from "../../interface/ui-ql-widget-type-registry-entry.interface";
import {UiQlWidgetBuildInput} from "../../interface/ui-ql-widget-build-input.interface";
import {UiQlWidgetBuildResult} from "../../interface/ui-ql-widget-build-result.interface";
import {UiQlWidgetBuildIssue} from "../../interface/ui-ql-widget-build-issue.interface";
import {UiQlWidgetBuildMode} from "../../interface/ui-ql-widget-build-mode.interface";
import {UiQlWidgetPipeType} from "../../interface/ui-ql-widget-pipe-type.interface";
import {UiQlWidgetParameterJson} from "../../interface/ui-ql-widget-parameter-json.interface";
import {UiQlWidgetColumn} from "../../interface/ui-ql-widget-column.interface";
import {UiQlWidgetColumns} from "./ui-ql-widget-columns";

/**
 * Turns a QL-Widget-Builder definition — a policy-checked {@code QlQueryDto} plus widget type and
 * mapping — into a {@code WidgetData}. It is the ONE builder behind the Builder preview, the
 * Playground and (Phase 1b) the Alpha Home tile (requirement.md Geschaeftsregel 5), so all three
 * compute exactly the same request.
 *
 * What it decides, and nobody else:
 * <ul>
 *   <li><b>widget</b> — only through {@code WidgetFactory}, per type via
 *       {@link UI_QL_WIDGET_TYPE_REGISTRY} (a new type = one registry entry);</li>
 *   <li><b>pipe</b> (spike S1) — a definition with a bounded-external side-car join goes with the
 *       {@code simple} pipe (the backend {@code QlPipeBoundedExternalGuard} rejects
 *       {@code view}/{@code object} for it), everything else with {@code view};</li>
 *   <li><b>count</b> (api-contract.md Q-A) — side-car or groupBy definitions ask for NO total
 *       ({@code includeCount=false}, no paginator, "Anzahl nicht verfügbar" + reason); all others
 *       do;</li>
 *   <li><b>paging</b> — preview: the definition's own limit, capped at 25; tile per size, clamped
 *       to the policy (table/list ≤ 100, groupBy ≤ 500);</li>
 *   <li><b>columns</b> — the result columns in definition order ({@link UiQlWidgetColumns}): the
 *       table/list layout, the chart's axis/legend labels, and the request-level {@code label} of
 *       aggregate and date-group fields (the engine echoes it as the form label).</li>
 * </ul>
 *
 * The input query is never mutated: the widget writes paging/sorting/filters into its request on
 * every reload, so it gets its own copy.
 */
@Injectable({
    providedIn: 'root'
})
export class UiQlWidgetDataBuilder {

    static readonly SCHEMA_VERSION = 2;

    /** requirement.md Frontend "Vorschau: … Seitengroesse 25". */
    static readonly PREVIEW_PAGE_SIZE = QL_BUILDER_POLICY_LIMITS.defaultPreviewPageSize;

    /** Tile page size of a table/list per tile size name, unless `pagingBySize` says otherwise. */
    static readonly DEFAULT_TILE_PAGE_SIZE: { [size: string]: number } = {
        s1x1: 5,
        s2x2: 10,
        s4x2: 10,
        s4x4: 25,
    };

    /**
     * Tile page size of a groupBy definition (chart/category/grouped table). Always the policy
     * maximum: a chart that silently drops groups beyond a page would show a WRONG picture, and a
     * group row is cheap.
     */
    static readonly DEFAULT_TILE_PAGE_SIZE_GROUP_BY = QL_BUILDER_POLICY_LIMITS.maxPagingGroupBy;

    /** Query (and therefore pipe) name used when the definition carries none. */
    static readonly DEFAULT_QUERY_NAME = 'definition';

    static readonly DEFAULT_NAME = 'Vorschau';

    static readonly NO_DATA_TEXT = 'Keine Daten gefunden. Passen Sie Filter oder Verknüpfungen der Abfrage an.';

    static readonly NO_COUNT_REASON_GROUP_BY =
        'Gruppierte Abfragen liefern eine Zeile je Gruppe, aber keine Gesamtanzahl.';

    static readonly NO_COUNT_REASON_SIDE_CAR =
        'Abfragen mit externen Zusatzfeldern (Seitenwagen) liefern keine Gesamtanzahl.';

    /**
     * Builds the widget of a definition.
     *
     * @param input the definition and where it is shown
     * @return the widget plus every derived fact; {@code widgetData === null} when {@code errors}
     *         is non-empty
     */
    build(input: UiQlWidgetBuildInput): UiQlWidgetBuildResult {

        const widgetType = input?.widgetType;
        const query = input?.qlQuery;

        if (!query?.start?.name || !query.start.as) {
            return UiQlWidgetDataBuilder.failed(widgetType, [{
                code: 'definitionMissing',
                path: 'qlQuery.start',
                message: 'Die Abfrage hat keine Start-Entität.',
            }]);
        }

        const entry = UiQlWidgetDataBuilder.registryEntry(widgetType);
        if (!entry) {
            return UiQlWidgetDataBuilder.failed(widgetType, [{
                code: 'schemaUnsupported',
                path: 'widgetType',
                message: `Widget-Typ nicht unterstützt: ${widgetType}`,
            }]);
        }

        const isGroupBy = query.start.type === 'groupBy';
        const sideCarJoins = UiQlWidgetDataBuilder.collectSideCarJoins(query);
        const hasSideCar = sideCarJoins.length > 0;
        const model = UiQlWidgetDataBuilder.tryToModel(query);

        const noCount = model ? QlBuilderQueryConverter.requiresNoCount(model) : (isGroupBy || hasSideCar);
        const envBound = model
            ? QlBuilderQueryConverter.deriveEnvBound(model)
            : (hasSideCar || UiQlWidgetDataBuilder.hasGenericField(query));
        const pipe: UiQlWidgetPipeType = hasSideCar ? 'simple' : 'view';
        const pageSize = UiQlWidgetDataBuilder.pageSize(input, isGroupBy);
        const columns = UiQlWidgetColumns.derive(query, {model: input.model, labels: input.labels});

        const uniqueness = UiQlWidgetDataBuilder.sideCarUniqueness(sideCarJoins, input.sideCars);

        const base: UiQlWidgetBuildResult = {
            widgetData: null,
            request: null,
            widgetType,
            pipe,
            includeCount: !noCount,
            countAvailable: !noCount,
            countUnavailableReason: noCount ? UiQlWidgetDataBuilder.noCountReason(isGroupBy) : null,
            pageSize,
            envBound,
            hasSideCar,
            isGroupBy,
            sideCarNotUniqueJoins: uniqueness.notUnique,
            sideCarUniquenessKnown: uniqueness.known,
            errors: [],
        };

        const mapping: UiQlWidgetMapping = input.mapping ?? {};
        const errors = UiQlWidgetDataBuilder.validate(widgetType, mapping, query, isGroupBy, hasSideCar);
        if (errors.length) {
            return {...base, errors};
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: QL-Widget-Builder request of every widget type - one query, one pipe; view pipe
         *   by default, simple pipe for bounded-external side-car definitions (S1), includeCount
         *   false for groupBy and side-car definitions (api-contract.md Q-A).
         */
        const request = QlWidgetFactory.createWidgetRequest(
            UiQlWidgetDataBuilder.copyQuery(query, pageSize, columns),
            undefined,
            {
                pipeType: pipe,
                includeCount: !noCount,
                includeRuntimeMetrics: input.mode === 'preview',
            });

        if (input.mode === 'tile') {
            // the tile paints its own error state (ui-home-tile-base) - no global toast on top
            request.suppressErrorToast = true;
        }

        const widgetData = entry.create({
            alias: input.alias || `ui.ql.preview.${widgetType}.builder`,
            name: input.name || UiQlWidgetDataBuilder.DEFAULT_NAME,
            entity: query.start.name,
            noDataText: UiQlWidgetDataBuilder.NO_DATA_TEXT,
            request,
            params: UiQlWidgetDataBuilder.commonParams(input, pageSize),
            mapping,
            columns,
        });

        return {...base, widgetData, request};
    }

    /**
     * Builds the widget of a stored {@code ui.UiWidget} with {@code dataSource = qlQuery}: parses
     * {@code qlQueryJson} and the schema-2 {@code parameterJson}, then {@link build}.
     *
     * @param uiWidget the stored widget
     * @param options  where it is shown; {@code size}/{@code contentHeightPx} only matter for tiles
     */
    buildFromUiWidget(
        uiWidget: UiWidgetDto,
        options: {
            mode: UiQlWidgetBuildMode;
            size?: UiHomeTileSizeEnum;
            contentHeightPx?: number;
            sideCars?: CoreMetaExternalSideCarDto[];
        }): UiQlWidgetBuildResult {

        if (!uiWidget) {
            return UiQlWidgetDataBuilder.failed(null, [{
                code: 'definitionMissing',
                path: 'uiWidget',
                message: 'Zu dieser Kachel gehört keine Widget-Beschreibung.',
            }]);
        }

        if (uiWidget.dataSource !== UiDataSourceEnum.qlQuery) {
            return UiQlWidgetDataBuilder.failed(null, [{
                code: 'dataSourceMismatch',
                path: 'uiWidget.dataSource',
                message: `Das Widget „${uiWidget.alias}" ist kein QL-Widget.`,
            }]);
        }

        let qlQuery: QlQueryDto;
        let json: UiQlWidgetParameterJson;
        try {
            qlQuery = JSON.parse(uiWidget.qlQueryJson ?? '');
        } catch (error) {
            return UiQlWidgetDataBuilder.failed(null, [{
                code: 'definitionUnreadable',
                path: 'uiWidget.qlQueryJson',
                message: `Die QL-Definition des Widgets „${uiWidget.alias}" ist kein gültiges JSON.`,
            }]);
        }
        try {
            json = JSON.parse(uiWidget.parameterJson ?? '');
        } catch (error) {
            return UiQlWidgetDataBuilder.failed(null, [{
                code: 'definitionUnreadable',
                path: 'uiWidget.parameterJson',
                message: `Die Widget-Konfiguration des Widgets „${uiWidget.alias}" ist kein gültiges JSON.`,
            }]);
        }

        if (json?.schema !== UiQlWidgetDataBuilder.SCHEMA_VERSION) {
            return UiQlWidgetDataBuilder.failed(json?.widgetType ?? null, [{
                code: 'schemaUnsupported',
                path: 'uiWidget.parameterJson.schema',
                message: `Unbekanntes Schema: ${json?.schema}`,
            }]);
        }

        return this.build({
            qlQuery,
            widgetType: json.widgetType,
            mapping: json.mapping ?? {},
            mode: options.mode,
            size: options.size,
            contentHeightPx: options.contentHeightPx,
            pagingBySize: json.pagingBySize,
            alias: uiWidget.alias,
            name: uiWidget.name,
            sideCars: options.sideCars,
        });
    }

    /**
     * Tile entry point for Phase 1b ({@code UiHomeWidgetDataBuilder.build} switches here for
     * {@code dataSource = qlQuery}): the widget, or the German reason as
     * {@link UiHomeWidgetBuildError} the error tile already knows how to show.
     */
    buildForTile(uiWidget: UiWidgetDto, size: UiHomeTileSizeEnum, contentHeightPx?: number): WidgetData {

        const result = this.buildFromUiWidget(uiWidget, {mode: 'tile', size, contentHeightPx});

        if (!result.widgetData) {
            throw new UiHomeWidgetBuildError(result.errors.map(error => error.message).join(' '));
        }

        return result.widgetData;
    }

    // ------------------------------------------------------------------
    // derivations (static, pure - also used by the preview)
    // ------------------------------------------------------------------

    /** Every join that carries {@code joinParameters} — a bounded-external side-car join. */
    static collectSideCarJoins(query: QlQueryDto): QlJoinDto[] {
        const found: QlJoinDto[] = [];
        const walk = (joins: QlJoinDto[] | undefined): void => {
            for (const join of joins ?? []) {
                if (join.joinParameters && Object.keys(join.joinParameters).length > 0) {
                    found.push(join);
                }
                walk(join.joins);
            }
        };
        walk(query?.start?.joins);
        return found;
    }

    /** The pipe a definition needs (S1): {@code simple} with a side-car join, else {@code view}. */
    static derivePipe(query: QlQueryDto): UiQlWidgetPipeType {
        return UiQlWidgetDataBuilder.collectSideCarJoins(query).length > 0 ? 'simple' : 'view';
    }

    /**
     * Result column keys ({@code <as>.<as-or-name>}) of a groupBy definition — the keys a chart or
     * category mapping must name. {@code null} for an entity definition (its columns come from the
     * engine's form, not from explicit fields), so no membership check is possible there.
     */
    static resultKeys(query: QlQueryDto): string[] | null {
        if (query?.start?.type !== 'groupBy') {
            return null;
        }
        const keys: string[] = [];
        const add = (as: string, fields: QlQueryContentFieldDto[] | undefined): void => {
            for (const field of fields ?? []) {
                const key = field.as || field.name;
                if (key) {
                    keys.push(`${as}.${key}`);
                }
            }
        };
        const walk = (joins: QlJoinDto[] | undefined): void => {
            for (const join of joins ?? []) {
                add(join.as, join.fields);
                walk(join.joins);
            }
        };
        add(query.start.as, query.start.fields);
        walk(query.start.joins);
        return keys;
    }

    /**
     * Best-effort builder model of a raw definition ({@code QlBuilderQueryConverter.fromQlQuery}),
     * {@code null} when the converter cannot map it — for callers that only hold a
     * {@code QlQueryDto} (JSON tab) but want the model-based labels.
     */
    static modelOf(query: QlQueryDto): QlBuilderModel | null {
        return UiQlWidgetDataBuilder.tryToModel(query);
    }

    // ------------------------------------------------------------------
    // internals
    // ------------------------------------------------------------------

    private static validate(
        widgetType: UiQlWidgetType,
        mapping: UiQlWidgetMapping,
        query: QlQueryDto,
        isGroupBy: boolean,
        hasSideCar: boolean): UiQlWidgetBuildIssue[] {

        const entry = UiQlWidgetDataBuilder.registryEntry(widgetType);
        const errors: UiQlWidgetBuildIssue[] = [];

        if (entry.requiresGroupBy && !isGroupBy) {
            errors.push({
                code: 'groupByRequired',
                path: 'qlQuery.start.type',
                message: 'Diagramm und Kategorie brauchen eine gruppierte Abfrage.',
            });
        }

        if (hasSideCar && !entry.allowsSideCar) {
            errors.push({
                code: 'sideCarInGroupBy',
                path: 'qlQuery.start.joins',
                message: 'Externe Zusatzfelder (Seitenwagen) sind nur in Tabelle und Liste möglich.',
            });
        }

        const values = mapping as { [key: string]: unknown };
        const missing = entry.requiredMappingKeys.filter(key => {
            const value = values[key];
            return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
        });

        for (const key of missing) {
            errors.push({
                code: 'mappingInvalid',
                path: `mapping.${key}`,
                message: `Die Zuordnung „${UiQlWidgetMappingKeyLabels.label(key)}" fehlt.`,
            });
        }

        if (!missing.length) {
            errors.push(...entry.validateMapping(mapping, UiQlWidgetDataBuilder.resultKeys(query)));
        }

        return errors;
    }

    private static noCountReason(isGroupBy: boolean): string {
        return isGroupBy
            ? UiQlWidgetDataBuilder.NO_COUNT_REASON_GROUP_BY
            : UiQlWidgetDataBuilder.NO_COUNT_REASON_SIDE_CAR;
    }

    /** Own-property lookup, so `constructor`/`toString` never pass for a widget type. */
    private static registryEntry(widgetType: UiQlWidgetType): UiQlWidgetTypeRegistryEntry | null {
        return widgetType && Object.prototype.hasOwnProperty.call(UI_QL_WIDGET_TYPE_REGISTRY, widgetType)
            ? UI_QL_WIDGET_TYPE_REGISTRY[widgetType]
            : null;
    }

    private static tryToModel(query: QlQueryDto): QlBuilderModel | null {
        try {
            return QlBuilderQueryConverter.fromQlQuery(query).model;
        } catch (error) {
            return null;
        }
    }

    /** Fallback for a definition the converter cannot map: any {@code g_<id>:…} field name. */
    private static hasGenericField(query: QlQueryDto): boolean {
        const isGeneric = (fields: QlQueryContentFieldDto[] | undefined): boolean =>
            (fields ?? []).some(field => QlBuilderFieldPatterns.generic.test(field.name ?? ''));
        const walk = (joins: QlJoinDto[] | undefined): boolean =>
            (joins ?? []).some(join => isGeneric(join.fields) || walk(join.joins));
        return isGeneric(query.start.fields) || walk(query.start.joins);
    }

    /**
     * Preview: the definition's own limit ({@code paging.pageSize}, what the builder's "Zeilen"
     * field says), capped at {@link PREVIEW_PAGE_SIZE} — a limit of 10 shows 10 rows, a limit of
     * 100 shows 25. Tile: per tile size, clamped to the policy maximum.
     */
    private static pageSize(input: UiQlWidgetBuildInput, isGroupBy: boolean): number {

        const max = isGroupBy ? QL_BUILDER_POLICY_LIMITS.maxPagingGroupBy : QL_BUILDER_POLICY_LIMITS.maxPagingEntity;

        let requested: number;
        if (input.mode === 'preview') {
            const limit = input.qlQuery?.paging?.pageSize;
            requested = limit > 0
                ? Math.min(limit, UiQlWidgetDataBuilder.PREVIEW_PAGE_SIZE)
                : UiQlWidgetDataBuilder.PREVIEW_PAGE_SIZE;
        } else {
            const sizeName = UiHomeTileSizeEnum[input.size ?? UiHomeTileSizeEnum.s2x2];
            requested = input.pagingBySize?.[sizeName]
                ?? (isGroupBy
                    ? UiQlWidgetDataBuilder.DEFAULT_TILE_PAGE_SIZE_GROUP_BY
                    : UiQlWidgetDataBuilder.DEFAULT_TILE_PAGE_SIZE[sizeName]
                    ?? UiQlWidgetDataBuilder.DEFAULT_TILE_PAGE_SIZE['s2x2']);
        }

        return Math.max(1, Math.min(requested, max));
    }

    private static commonParams(input: UiQlWidgetBuildInput, pageSize: number): WidgetDataParam[] {

        const params: WidgetDataParam[] = [WidgetDataParam.create('paging', pageSize)];

        if (input.mode === 'tile') {
            params.push(WidgetDataParam.create('showHeader', false));
            params.push(WidgetDataParam.create(
                'widgetHeight',
                UiHomeTileMetrics.widgetHeight(input.size ?? UiHomeTileSizeEnum.s2x2, input.contentHeightPx)));
        }

        return params;
    }

    /**
     * The widget's own copy: the start tree deep-copied (plain JSON, it only ever travels to the
     * backend), top-level filters/sortings as fresh arrays of the SAME instances (the widget's
     * filter UI works with them), paging forced to the effective page size, and the German label
     * of every aggregate/date-group start field written into the field's {@code label} — the
     * engine builds the form field's {@code uiLabel} from it ({@code QlDtoFormService}), so the
     * raw table and the chart see "Anzahl" instead of "e.count" even without a layout.
     */
    private static copyQuery(query: QlQueryDto, pageSize: number, columns: UiQlWidgetColumn[] = []): QlQueryDto {

        const copy: QlQueryDto = {
            ...query,
            name: query.name || UiQlWidgetDataBuilder.DEFAULT_QUERY_NAME,
            start: JSON.parse(JSON.stringify(query.start)),
            paging: PagingDto.create(0, pageSize),
        };

        if (query.filters) {
            copy.filters = [...query.filters];
        }
        if (query.sortings) {
            copy.sortings = [...query.sortings];
        }

        for (const field of copy.start.fields ?? []) {
            if (field.label || (field.fieldType !== 'function' && field.fieldType !== 'dateGroup')) {
                continue;
            }
            const label = UiQlWidgetColumns.find(columns, `${copy.start.as}.${field.as || field.name}`)?.label;
            if (label) {
                field.label = label;
            }
        }

        return copy;
    }

    private static sideCarUniqueness(
        sideCarJoins: QlJoinDto[],
        sideCars: CoreMetaExternalSideCarDto[] | undefined): { notUnique: string[]; known: boolean } {

        if (!sideCarJoins.length) {
            return {notUnique: [], known: true};
        }
        if (!sideCars) {
            return {notUnique: [], known: false};
        }

        const notUnique = sideCarJoins
            .map(join => join.name)
            .filter(name => sideCars.some(sideCar =>
                (sideCar.joinName === name || sideCar.instanceAlias === name) && sideCar.uniqueGuaranteed === false));

        return {notUnique, known: true};
    }

    private static failed(widgetType: UiQlWidgetType | null, errors: UiQlWidgetBuildIssue[]): UiQlWidgetBuildResult {
        return {
            widgetData: null,
            request: null,
            widgetType,
            pipe: 'view',
            includeCount: false,
            countAvailable: false,
            countUnavailableReason: null,
            pageSize: 0,
            envBound: false,
            hasSideCar: false,
            isGroupBy: false,
            sideCarNotUniqueJoins: [],
            sideCarUniquenessKnown: true,
            errors,
        };
    }
}
